import 'twin.macro';
import { v4 as uuid } from 'uuid';
import React, { RefObject } from 'react';
import { Field } from 'react-final-form';
import Button from '@teetsh/app/src/components/Buttons/Button';
import {
  ArrowTopRightOnSquareIcon,
  CheckIcon,
  PlusIcon as Plus,
} from '@heroicons/react/24/outline';
import { useSchoolSchoolyear } from '@teetsh/app/src/containers/App/context/SchoolyearSchoolProvider';
import { Classroom } from '@teetsh/app/src/types/classroom';
import {
  BILAN_EVENT,
  CLASSIC_EVENT,
  RAPPEL_EVENT,
} from '@teetsh/app/src/containers/constants';
import { AddToSeanceButtons } from 'containers/CahierJournal/EventModal/Sidebar/AddToSeanceButtons';
import { useCreateEventCustomFieldDefinition } from 'queries/events/customfields/useCreateEventCustomFieldDefinition';
import { useTranslation } from 'react-i18next';
import { useUser } from '@teetsh/app/src/containers/App/context/useUser';
import DeleteButton from 'components/Buttons/DeleteButton';
import { CJEventType } from 'types/event';
import { useUpdateSeance } from 'components/SeanceTypeSwitcher/useUpdateSeance';
import { CustomFieldForm } from 'components/CustomFieldForm/CustomFieldForm';
import { Radios } from 'components/Form/Radios';
import { Checkboxes } from 'components/Form/Checkboxes';
import { CustomFieldsButtons } from './CustomFieldsButtons';
import tw from 'twin.macro';
import { Legend } from 'components/Form/FormHelper/Fieldset/Legend';
import { AttachmentButton } from './AttachmentButton';

interface Props {
  ateliersRef?: RefObject<HTMLDivElement>;
  materialInputRef?: RefObject<HTMLInputElement>;
  attachmentInputRef?: RefObject<HTMLInputElement>;
  customFieldsInputRefs?: RefObject<HTMLInputElement[]>;
  openCreateFdpDrawer: () => void;
  classrooms: Classroom[];
  className?: string;
  type: CJEventType;
  dirty: boolean;
  cycle: string;
  variant: 'desktop_sidebar' | 'mobile_tab';
  onAddToSeanceCallback?: () => void;
}

type SeanceEventType = 'rappel' | 'bilan' | 'break' | 'classic';

export function availableTypes(type: CJEventType): SeanceEventType[] {
  if (type === 'bilan') {
    return ['bilan'];
  }
  if (type === 'rappel') {
    return ['rappel'];
  }
  return ['classic', 'break'];
}

function CreateCustomFieldForm({ onClose }: { onClose: () => void }) {
  const { school, schoolyear } = useSchoolSchoolyear();
  const { mutateAsync: createEventCustomField } =
    useCreateEventCustomFieldDefinition({
      schoolId: school.id,
      schoolyearId: schoolyear.id,
    });

  return (
    <CustomFieldForm
      onClose={onClose}
      onSubmit={v =>
        createEventCustomField({
          name: v.name,
          pluralName: v.pluralName || v.name,
          options: v.options,
          type: v.type,
          targets: ['cahierJournal'],
          id: uuid(),
        })
      }
      availableFieldTypes={['text', 'multiselect', 'select']}
      cycles={[]} // cycles will be used to suggest custom field presets, but not yet useful
      initialValues={{
        name: '',
        pluralName: '',
        type: 'text',
        options: [],
      }}
      // TODO handle targets : https://linear.app/teetsh/issue/TSH-1193/champs-personnalisables-ajouter-possibilite-de-definir-les-target
    />
  );
}

export function Sidebar(props: Props) {
  const {
    className,
    classrooms,
    openCreateFdpDrawer,
    type,
    dirty,
    cycle,
    variant,
    onAddToSeanceCallback,
  } = props;
  const { school } = useSchoolSchoolyear();
  const [editNewFieldForm, setEditNewFieldForm] = React.useState(false);
  const { t } = useTranslation(['common', 'cahierJournal']);
  const user = useUser();
  const enableEdit = !user.guestMode?.enabled;
  const updateSeance = useUpdateSeance();
  const availableTypesValue = availableTypes(type);

  return (
    <aside className={className}>
      <div tw="flex flex-col gap-6">
        <div
          css={[
            tw`flex flex-col gap-6`,
            variant === 'mobile_tab' && tw`grid grid-cols-2`,
          ]}
        >
          <Field
            name="type"
            render={({ input: { value, onChange } }) => {
              if (availableTypesValue.length <= 1) {
                return null;
              }
              return (
                <Radios
                  options={availableTypesValue.map(v => ({
                    label: t(`cahierJournal:seance.types.${v}`),
                    value: v,
                  }))}
                  label={t('cahierJournal:seance.modal.type')}
                  onChange={newValue =>
                    updateSeance({ value: newValue, onChange })
                  }
                  value={value}
                  legendVariant={
                    variant === 'mobile_tab' ? 'moderate' : undefined
                  }
                />
              );
            }}
          />
          {school.classroomsOrg === 'unified' && classrooms.length > 1 && (
            <div data-testid="classes-section">
              <Field
                name="classroomIds"
                render={({ input: { value, onChange } }) => (
                  <Checkboxes
                    condensed
                    minCheckedOptions={1}
                    value={value}
                    onChange={onChange}
                    label={t('cahierJournal:seance.modal.classrooms')}
                    options={classrooms.map(cl => ({
                      label: cl.name,
                      value: cl.id,
                    }))}
                    legendVariant={
                      variant === 'mobile_tab' ? 'moderate' : undefined
                    }
                  />
                )}
              />
            </div>
          )}
        </div>
        {type === CLASSIC_EVENT && (
          <div>
            <Legend variant={variant === 'mobile_tab' ? 'moderate' : 'quiet'}>
              {t('cahierJournal:seance.modal.elementToAdd')}
            </Legend>
            <div tw="flex flex-wrap gap-2">
              <AddToSeanceButtons
                cycle={cycle}
                ateliersRef={props.ateliersRef}
                materialInputRef={props.materialInputRef}
                attachmentInputRef={props.attachmentInputRef}
                onAddToSeanceCallback={onAddToSeanceCallback}
              />
            </div>
          </div>
        )}
        {(type === BILAN_EVENT || type === RAPPEL_EVENT) && (
          <div>
            <Legend variant={variant === 'mobile_tab' ? 'moderate' : 'quiet'}>
              {t('cahierJournal:seance.modal.elementToAdd')}
            </Legend>
            <div tw="flex flex-wrap gap-2">
              <AttachmentButton
                formFieldName="attachments"
                inputRef={props.attachmentInputRef}
                onAddToSeanceCallback={onAddToSeanceCallback}
              />
            </div>
          </div>
        )}
        {type === CLASSIC_EVENT && (
          <div>
            {enableEdit && (
              <Legend variant={variant === 'mobile_tab' ? 'moderate' : 'quiet'}>
                {t('cahierJournal:seance.modal.customElements')}
              </Legend>
            )}
            <div tw="flex flex-wrap gap-2">
              {editNewFieldForm ? (
                <CreateCustomFieldForm
                  onClose={() => setEditNewFieldForm(false)}
                />
              ) : (
                <>
                  <CustomFieldsButtons
                    formFieldName="customFields"
                    inputRefs={props.customFieldsInputRefs}
                    enableEdit={!user.guestMode?.enabled}
                    onAddToSeanceCallback={onAddToSeanceCallback}
                  />
                  {enableEdit && (
                    <Button
                      onClick={() => setEditNewFieldForm(true)}
                      minimal
                      small
                      icon={Plus}
                      alignText="left"
                      tw="text-neutral-moderate"
                    >
                      {t('common:customFields.actions.customElement')}
                    </Button>
                  )}
                </>
              )}
            </div>
          </div>
        )}
        {type === CLASSIC_EVENT && enableEdit && (
          <div>
            <Legend variant={variant === 'mobile_tab' ? 'moderate' : 'quiet'}>
              {t('cahierJournal:seance.modal.createFromSeance')}
            </Legend>
            <DeleteButton
              onClick={() => openCreateFdpDrawer()}
              withConfirmation={dirty}
              popoverText={
                <p tw="text-sm">
                  {t('cahierJournal:messages.ongoingModificationSave')}
                </p>
              }
              confirmationButtonProps={{
                small: false,
                intent: 'primary',
                icon: CheckIcon,
                'aria-label': 'Save event',
              }}
              confirmationText={t('common:buttons.save')}
              intent="none"
              buttonText={t('cahierJournal:actions.createFdpFromSeance')}
              alignText="left"
              icon={ArrowTopRightOnSquareIcon}
            />
          </div>
        )}
      </div>
    </aside>
  );
}
